// Two-pane YouTube playlist player — the port of live's
// z-youtube-playlist-player-1 plugin (playlist.php): video titles fetched
// client-side from the YouTube Data API with the same public key live embeds
// in its own page, playback through the YouTube IFrame API, playVideoAt(i)
// on click, active row highlighted as the player cues each video.
//
// Failure is graceful in both directions: if the Data API call fails (quota,
// key rotation) the titles pane stays empty and collapses via CSS :empty,
// leaving YouTube's own playlist player fully usable; if JS never runs, the
// <noscript> iframe in the section renders the plain embed.

let apiPromise = null;

function loadIframeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) {
    apiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prev?.();
        resolve(window.YT);
      };
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    });
  }
  return apiPromise;
}

class ZagerPlaylistPlayer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.listEl = this.querySelector('[data-playlist-titles]');
    this.frameEl = this.querySelector('[data-playlist-frame]');
    if (!this.dataset.playlistId || !this.frameEl) return;

    // The block sits far below the fold; neither YouTube script should load
    // until it is near the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          this.init();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(this);
  }

  async init() {
    this.renderTitles();
    const YT = await loadIframeApi();
    const mount = document.createElement('div');
    this.frameEl.replaceChildren(mount);
    this.player = new YT.Player(mount, {
      playerVars: {
        autoplay: 0,
        controls: 1,
        playsinline: 1,
        rel: 0,
        listType: 'playlist',
        list: this.dataset.playlistId,
      },
      events: {
        onReady: () => this.syncActive(),
        onStateChange: () => this.syncActive(),
      },
    });
  }

  async renderTitles() {
    if (!this.dataset.apiKey || !this.listEl) return;
    const url =
      'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50' +
      `&playlistId=${encodeURIComponent(this.dataset.playlistId)}` +
      `&key=${encodeURIComponent(this.dataset.apiKey)}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const json = await res.json();
      const rows = (json.items ?? []).map((item, index) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'zager-playlist__title';
        row.textContent = item.snippet?.title ?? '';
        row.addEventListener('click', () => {
          this.player?.playVideoAt(index);
          this.setActive(index);
        });
        return row;
      });
      this.listEl.replaceChildren(...rows);
      this.setActive(0);
    } catch {
      // Titles stay empty; the player alone is still the full playlist.
    }
  }

  setActive(index) {
    this.listEl?.querySelectorAll('.zager-playlist__title').forEach((el, i) => {
      el.classList.toggle('zager-playlist__title--active', i === index);
    });
  }

  syncActive() {
    const index = this.player?.getPlaylistIndex?.();
    if (typeof index === 'number' && index >= 0) this.setActive(index);
  }
}

if (!customElements.get('zager-playlist-player')) {
  customElements.define('zager-playlist-player', ZagerPlaylistPlayer);
}
